import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  HeroSelectField,
  Input,
  LoadingScreen,
  PageHeader,
  Switch,
} from "../../components/Ui";
import { useToast } from "../../contexts/ToastContext";
import {
  useBranchInventory,
  useBusinessBranches,
  useInventoryAlerts,
  useInventoryBranchMovements,
  useInventorySettings,
  useUpdateBranchInventory,
  useUpdateInventorySettings,
} from "../../hooks/businessData";
import type {
  BranchInventoryRow,
  InventoryMode,
  InventoryMovementType,
} from "../../../shared/types";
import { InventoryMovementModal } from "./InventoryMovementModal";
import { InventoryQuickAdjustModal } from "./InventoryQuickAdjustModal";
import { InventoryTabs } from "./InventoryTabs";

const modeLabels: Record<InventoryMode, string> = {
  unlimited: "Sin límite",
  template: "Plantilla",
  external: "Externo",
};

const movementTypeLabels: Record<InventoryMovementType, string> = {
  sale: "Venta",
  adjustment: "Ajuste",
  external_sync: "Importación externa",
  reset: "Reset diario",
  soldout_signal: "Señal agotado",
  cancellation: "Cancelación",
};

type ProductDraft = {
  mode: InventoryMode;
  stockQuantity: string;
  externalCode: string;
};
type RowSaveState = {
  status: "idle" | "saving" | "ok" | "error";
  error?: string;
};
type InventoryIssueBadge = { label: string; tone: "slate" | "amber" | "red" };

const normalizeExternalCode = (value: string | null | undefined) =>
  (value ?? "").trim() || null;

function getInventoryIssues(
  row: BranchInventoryRow,
  lowStockThreshold: number,
): InventoryIssueBadge[] {
  const issues: InventoryIssueBadge[] = [];
  if (row.effectiveStock !== null) {
    if (row.effectiveStock < 0) issues.push({ label: "Negativo", tone: "red" });
    else if (row.effectiveStock === 0)
      issues.push({ label: "Agotado", tone: "red" });
    else if (row.effectiveStock <= lowStockThreshold)
      issues.push({ label: "Crítico", tone: "amber" });
  }
  if (row.mode === "template" && row.effectiveStock === null)
    issues.push({ label: "Sin plantilla hoy", tone: "amber" });
  if (row.mode === "external" && !row.externalCode)
    issues.push({ label: "Sin código externo", tone: "amber" });
  if (row.mode === "external" && row.stockQuantity <= 0)
    issues.push({ label: "Sin stock base", tone: "red" });
  return issues;
}

function formatMovementDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("es", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function signedQuantity(quantity: number) {
  return quantity > 0 ? `+${quantity}` : String(quantity);
}

export default function InventoryPage() {
  const { show } = useToast();
  const { data: branches = [], isLoading: branchesLoading } =
    useBusinessBranches();
  const [branchId, setBranchId] = useState("");
  const selectedBranchId = branchId || branches[0]?.id || "";
  const { data: rows = [], isLoading } = useBranchInventory(selectedBranchId);
  const { data: activeAlerts = [], isLoading: alertsLoading } =
    useInventoryAlerts(selectedBranchId, "activa");
  const { data: branchMovements = [], isLoading: movementsLoading } =
    useInventoryBranchMovements(selectedBranchId, 10);
  const { data: inventorySettings } = useInventorySettings();
  const updateInventory = useUpdateBranchInventory();
  const updateInventorySettings = useUpdateInventorySettings();

  // Local edit drafts per row; original values come from `rows` (server snapshot).
  const [drafts, setDrafts] = useState<Record<string, ProductDraft>>({});
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    new Set<string>(),
  );
  const [rowStates, setRowStates] = useState<Record<string, RowSaveState>>({});
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [onlyModified, setOnlyModified] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [movementProduct, setMovementProduct] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [quickAdjustRow, setQuickAdjustRow] =
    useState<BranchInventoryRow | null>(null);
  const lowStockThreshold = inventorySettings?.lowStockThreshold ?? 3;
  const [thresholdDraft, setThresholdDraft] = useState("3");

  useEffect(() => {
    setThresholdDraft(String(lowStockThreshold));
  }, [lowStockThreshold]);

  // Drafts, selection and filters are branch-scoped: reset them when switching branches.
  useEffect(() => {
    setDrafts({});
    setSelectedIds(new Set<string>());
    setRowStates({});
    setSearch("");
    setCategory("");
    setOnlyModified(false);
    setOnlyIssues(false);
  }, [selectedBranchId]);

  const draftFor = (
    productId: string,
    fallback: BranchInventoryRow,
  ): ProductDraft =>
    drafts[productId] ?? {
      mode: fallback.mode,
      stockQuantity: String(fallback.stockQuantity),
      externalCode: fallback.externalCode ?? "",
    };

  const isRowDirty = (productId: string, row: BranchInventoryRow) => {
    const draft = draftFor(productId, row);
    return (
      draft.mode !== row.mode ||
      Number(draft.stockQuantity || 0) !== row.stockQuantity ||
      normalizeExternalCode(draft.externalCode) !==
        normalizeExternalCode(row.externalCode)
    );
  };

  const dirtyIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of rows)
      if (isRowDirty(row.productId, row)) ids.add(row.productId);
    return ids;
  }, [rows, drafts]);

  const updateDraft = (
    productId: string,
    fallback: BranchInventoryRow,
    patch: Partial<ProductDraft>,
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [productId]: { ...draftFor(productId, fallback), ...patch },
    }));
    setRowStates((prev) =>
      prev[productId]?.status === "idle"
        ? prev
        : { ...prev, [productId]: { status: "idle" } },
    );
  };

  const categories = useMemo(() => {
    const names = new Set<string>();
    for (const row of rows) if (row.categoryName) names.add(row.categoryName);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const searchQuery = search.trim().toLowerCase();

  // Filters apply to the view only; drafts/selection survive for hidden rows.
  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        if (searchQuery && !row.productName.toLowerCase().includes(searchQuery))
          return false;
        if (category && row.categoryName !== category) return false;
        if (onlyModified && !dirtyIds.has(row.productId)) return false;
        if (
          onlyIssues &&
          getInventoryIssues(row, lowStockThreshold).length === 0
        )
          return false;
        return true;
      }),
    [
      rows,
      searchQuery,
      category,
      onlyModified,
      onlyIssues,
      dirtyIds,
      lowStockThreshold,
    ],
  );

  const anySaving = Object.values(rowStates).some(
    (state) => state.status === "saving",
  );
  const visibleSelectedCount = visibleRows.filter((row) =>
    selectedIds.has(row.productId),
  ).length;
  const allVisibleSelected =
    visibleRows.length > 0 && visibleSelectedCount === visibleRows.length;
  const someVisibleSelected =
    visibleSelectedCount > 0 && visibleSelectedCount < visibleRows.length;
  const soldOutCount = rows.filter(
    (row) => row.effectiveStock !== null && row.effectiveStock <= 0,
  ).length;
  const criticalStockCount = rows.filter(
    (row) =>
      row.effectiveStock !== null &&
      row.effectiveStock > 0 &&
      row.effectiveStock <= lowStockThreshold,
  ).length;

  const saveLowStockThreshold = async () => {
    const nextThreshold = Number(thresholdDraft);
    if (
      !Number.isInteger(nextThreshold) ||
      nextThreshold < 1 ||
      nextThreshold > 999
    ) {
      show("El umbral debe ser un entero entre 1 y 999.", "error");
      return;
    }
    try {
      await updateInventorySettings.mutateAsync({
        lowStockThreshold: nextThreshold,
      });
      show("Umbral de stock crítico guardado.", "success");
    } catch (error) {
      show(
        error instanceof Error
          ? error.message
          : "No se pudo guardar el umbral.",
        "error",
      );
    }
  };

  const toggleRowSelected = (productId: string, isSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isSelected) next.add(productId);
      else next.delete(productId);
      return next;
    });
  };

  const toggleAllVisible = (isSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const row of visibleRows) {
        if (isSelected) next.add(row.productId);
        else next.delete(row.productId);
      }
      return next;
    });
  };

  // Per-row save through the existing PUT /api/business/inventory/:branchId/:productId.
  // Only dirty rows are ever sent; untouched rows are never included.
  const saveRows = async (productIds: string[]) => {
    if (!selectedBranchId) {
      show("Seleccioná una sucursal.", "warning");
      return;
    }
    const targets = productIds.filter((productId) => dirtyIds.has(productId));
    if (targets.length === 0) {
      show("No hay filas modificadas para guardar.", "info");
      return;
    }

    setRowStates((prev) => {
      const next = { ...prev };
      for (const productId of targets) next[productId] = { status: "saving" };
      return next;
    });

    const results = await Promise.allSettled(
      targets.map(async (productId) => {
        const row = rows.find((item) => item.productId === productId);
        if (!row) return;
        const draft = draftFor(productId, row);
        await updateInventory.mutateAsync({
          branchId: selectedBranchId,
          productId,
          payload: {
            mode: draft.mode,
            stockQuantity: Number(draft.stockQuantity || 0),
            externalCode: draft.externalCode.trim() || null,
          },
        });
      }),
    );

    const saved: string[] = [];
    const failed: Array<{ productId: string; message: string }> = [];
    results.forEach((result, index) => {
      const productId = targets[index]!;
      if (result.status === "fulfilled") saved.push(productId);
      else
        failed.push({
          productId,
          message:
            result.reason instanceof Error
              ? result.reason.message
              : "No se pudo guardar la fila.",
        });
    });

    setRowStates((prev) => {
      const next = { ...prev };
      for (const productId of saved) next[productId] = { status: "ok" };
      for (const { productId, message } of failed)
        next[productId] = { status: "error", error: message };
      return next;
    });
    if (saved.length > 0) {
      setDrafts((prev) => {
        const next = { ...prev };
        for (const productId of saved) delete next[productId];
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const productId of saved) next.delete(productId);
        return next;
      });
    }

    if (failed.length === 0)
      show(
        `${saved.length} ${saved.length === 1 ? "fila actualizada" : "filas actualizadas"}.`,
        "success",
      );
    else if (saved.length === 0) show(failed[0]!.message, "error");
    else
      show(
        `${saved.length} ${saved.length === 1 ? "fila actualizada" : "filas actualizadas"}, ${failed.length} con error.`,
        "error",
      );
  };

  // With a selection, only the selected rows are sent (still filtered to dirty);
  // without selection the action falls back to "save edited" (all dirty rows).
  const handleSaveSelection = () => {
    if (selectedIds.size > 0) void saveRows([...selectedIds]);
    else void saveRows([...dirtyIds]);
  };

  if (branchesLoading) return <LoadingScreen label="Cargando sucursales…" />;

  return (
    <div>
      <PageHeader
        title="Inventario por sucursal"
        description="Configurá el modo de stock, el stock externo y el código de integración por producto."
      />
      <InventoryTabs />
      <Card className="mb-5">
        <HeroSelectField
          label="Sucursal"
          value={selectedBranchId || null}
          onChange={(value) => setBranchId(String(value ?? ""))}
          options={branches.map((branch) => ({
            id: branch.id,
            label: branch.name,
          }))}
        />
      </Card>
      <div className="mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <Card className="min-h-32">
          <div className="text-sm font-bold text-[var(--app-brand-muted)]">
            Alertas activas
          </div>
          <div className="mt-2 text-3xl font-black text-[var(--app-brand-ink)]">
            {alertsLoading ? "—" : activeAlerts.length}
          </div>
          <p className="mt-1 text-xs font-bold text-[var(--app-brand-muted)]">
            Alertas de stock pendientes para esta sucursal.
          </p>
        </Card>
        <Card className="min-h-32">
          <div className="text-sm font-bold text-[var(--app-brand-muted)]">
            Agotados
          </div>
          <div className="mt-2 text-3xl font-black text-red-600">
            {isLoading ? "—" : soldOutCount}
          </div>
          <p className="mt-1 text-xs font-bold text-[var(--app-brand-muted)]">
            Productos con stock efectivo en 0 o menos.
          </p>
        </Card>
        <Card className="min-h-32">
          <div className="text-sm font-bold text-[var(--app-brand-muted)]">
            Stock crítico
          </div>
          <div className="mt-2 text-3xl font-black text-amber-600">
            {isLoading ? "—" : criticalStockCount}
          </div>
          <p className="mt-1 text-xs font-bold text-[var(--app-brand-muted)]">
            Productos con ≤ {lowStockThreshold} unidades disponibles.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
            <Input
              aria-label="Umbral de stock crítico"
              type="number"
              min={1}
              max={999}
              value={thresholdDraft}
              onChange={(event) => setThresholdDraft(event.target.value)}
              className="max-w-28"
            />
            <Button
              variant="secondary"
              busy={updateInventorySettings.isPending}
              onPress={() => void saveLowStockThreshold()}
            >
              Guardar umbral
            </Button>
          </div>
        </Card>
        <Card className="min-h-32 max-w-fit col-span-3 pb-0">
          <div className="text-sm font-bold text-[var(--app-brand-muted)]">
            Últimos movimientos
          </div>
          {movementsLoading ? (
            <p className="mt-3 text-sm font-bold text-[var(--app-brand-muted)]">
              Cargando…
            </p>
          ) : branchMovements.length === 0 ? (
            <p className="mt-3 text-sm font-bold text-[var(--app-brand-muted)]">
              Sin movimientos recientes
            </p>
          ) : (
              <ul className="mt-3 space-y-2 flex flex-row overflow-x-auto">
                {branchMovements.slice(0, 10).map((movement) => (
                  <li
                    key={movement.id}
                    className="rounded-2xl bg-[var(--app-brand-soft)] p-3 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2 font-black text-[var(--app-brand-ink)]">
                      <span>{movementTypeLabels[movement.type]}</span>
                      <span
                        className={
                          movement.quantity < 0
                            ? "text-red-600"
                            : movement.quantity > 0
                              ? "text-green-700"
                              : "text-[var(--app-brand-muted)]"
                        }
                      >
                        {signedQuantity(movement.quantity)}
                      </span>
                    </div>
                    <div className="mt-1 text-[var(--app-brand-muted)]">
                      {formatMovementDate(movement.createdAt)}
                    </div>
                    {movement.notes ? (
                      <div className="mt-1 line-clamp-2 text-[var(--app-brand-muted)]">
                        {movement.notes}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
          )}
        </Card>
      </div>
      {isLoading ? (
        <LoadingScreen label="Cargando inventario…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Sin productos"
          description="Agregá productos al menú para configurar inventario."
        />
      ) : (
        <Card>
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid gap-3 sm:grid-cols-2 lg:max-w-xl lg:flex-1">
              <Input
                aria-label="Buscar por nombre de producto"
                placeholder="Buscar por nombre…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <HeroSelectField
                aria-label="Filtrar por categoría"
                value={category || null}
                onChange={(value) => setCategory(String(value ?? ""))}
                options={[
                  { id: "", label: "Todas las categorías" },
                  ...categories.map((name) => ({ id: name, label: name })),
                ]}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone="amber">Modificadas: {dirtyIds.size}</Badge>
              <Badge tone="blue">Seleccionadas: {selectedIds.size}</Badge>
              <Switch
                label="Solo modificados"
                isSelected={onlyModified}
                onChange={setOnlyModified}
              />
              <Switch
                label="Solo problemas"
                isSelected={onlyIssues}
                onChange={setOnlyIssues}
              />
            </div>
            <Button
              busy={anySaving}
              disabled={dirtyIds.size === 0 && selectedIds.size === 0}
              onPress={handleSaveSelection}
            >
              {selectedIds.size > 0
                ? `Guardar seleccionados (${selectedIds.size})`
                : `Guardar editados (${dirtyIds.size})`}
            </Button>
          </div>
          <div className="overflow-x-auto">
            {visibleRows.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[var(--app-brand-line)] bg-[var(--app-brand-soft)] p-4 text-sm font-bold text-[var(--app-brand-muted)]">
                {onlyIssues
                  ? "Sin problemas de inventario con los filtros actuales."
                  : "Sin filas que coincidan con los filtros."}
              </div>
            ) : (
              <table className="w-full min-w-[960px] text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-[var(--app-brand-muted)]">
                  <tr>
                    <th className="w-10 py-2">
                      <Checkbox
                        aria-label="Seleccionar todas las filas visibles"
                        isSelected={allVisibleSelected}
                        isIndeterminate={someVisibleSelected}
                        onChange={toggleAllVisible}
                      />
                    </th>
                    <th className="py-2">Producto</th>
                    <th>Modo</th>
                    <th>Stock base</th>
                    <th>Consumido hoy</th>
                    <th>Stock efectivo</th>
                    <th>Código externo</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const draft = draftFor(row.productId, row);
                    const rowState = rowStates[row.productId];
                    const isDirty = dirtyIds.has(row.productId);
                    const issueBadges = getInventoryIssues(
                      row,
                      lowStockThreshold,
                    );
                    return (
                      <tr
                        key={row.productId}
                        className="border-t border-[var(--app-brand-line)] align-top"
                      >
                        <td className="py-3">
                          <Checkbox
                            aria-label={`Seleccionar ${row.productName}`}
                            isSelected={selectedIds.has(row.productId)}
                            onChange={(next) =>
                              toggleRowSelected(row.productId, next)
                            }
                          />
                        </td>
                        <td className="py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-black text-[var(--app-brand-ink)]">
                              {row.productName}
                            </span>
                            {isDirty ? (
                              <Badge tone="amber">Modificado</Badge>
                            ) : null}
                            {issueBadges.map((issue) => (
                              <Badge key={issue.label} tone={issue.tone}>
                                {issue.label}
                              </Badge>
                            ))}
                            {rowState?.status === "ok" ? (
                              <Badge tone="green">Guardado</Badge>
                            ) : null}
                            {rowState?.status === "error" ? (
                              <Badge tone="red">Error</Badge>
                            ) : null}
                          </div>
                          <div className="text-xs text-[var(--app-brand-muted)]">
                            {row.categoryName ?? "Sin categoría"}
                          </div>
                        </td>
                        <td className="py-3">
                          <HeroSelectField
                            className="min-w-36"
                            value={draft.mode}
                            onChange={(value) =>
                              updateDraft(row.productId, row, {
                                mode: String(
                                  value ?? "unlimited",
                                ) as InventoryMode,
                              })
                            }
                            options={Object.entries(modeLabels).map(
                              ([id, label]) => ({ id, label }),
                            )}
                          />
                        </td>
                        <td className="py-3">
                          <Input
                            className="max-w-32"
                            type="number"
                            min={0}
                            value={draft.stockQuantity}
                            onChange={(event) =>
                              updateDraft(row.productId, row, {
                                stockQuantity: event.target.value,
                              })
                            }
                          />
                        </td>
                        <td className="py-3">{row.consumedToday}</td>
                        <td className="py-3">
                          {row.effectiveStock === null ? (
                            <Badge>Sin límite</Badge>
                          ) : (
                            <Badge
                              tone={row.effectiveStock <= 0 ? "red" : "green"}
                            >
                              {row.effectiveStock}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3">
                          <Input
                            value={draft.externalCode}
                            onChange={(event) =>
                              updateDraft(row.productId, row, {
                                externalCode: event.target.value,
                              })
                            }
                            placeholder="external_code"
                          />
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {rowState?.status === "error" ? (
                              <span
                                className="max-w-44 truncate text-xs font-bold text-red-600"
                                title={rowState.error}
                              >
                                {rowState.error}
                              </span>
                            ) : null}
                            <Button
                              variant="secondary"
                              onPress={() =>
                                setMovementProduct({
                                  id: row.productId,
                                  name: row.productName,
                                })
                              }
                            >
                              Historial
                            </Button>
                            <Button
                              variant="secondary"
                              onPress={() => setQuickAdjustRow(row)}
                            >
                              Ajuste rápido
                            </Button>
                            <Button
                              variant="secondary"
                              busy={rowState?.status === "saving"}
                              disabled={!isDirty}
                              onPress={() => void saveRows([row.productId])}
                            >
                              Guardar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      )}
      {movementProduct ? (
        <InventoryMovementModal
          branchId={selectedBranchId}
          productId={movementProduct.id}
          productName={movementProduct.name}
          open={Boolean(movementProduct)}
          onOpenChange={(open) => {
            if (!open) setMovementProduct(null);
          }}
        />
      ) : null}
      {quickAdjustRow ? (
        <InventoryQuickAdjustModal
          branchId={selectedBranchId}
          productId={quickAdjustRow.productId}
          productName={quickAdjustRow.productName}
          row={quickAdjustRow}
          open={Boolean(quickAdjustRow)}
          onOpenChange={(open) => {
            if (!open) setQuickAdjustRow(null);
          }}
        />
      ) : null}
    </div>
  );
}
